import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { inputCls, inputErrCls, FieldError, labelCls, btnPrimary } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { toast } from "sonner";

export default function StockAdjustments() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const [pid, setPid] = useState("");
  const [type, setType] = useState("in");
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("");

  const fe = useFormErrors();
  const adj = useMutation({
    mutationFn: () => api.post("/stock/adjust", { product_id: pid, type, quantity: parseFloat(qty), reason }),
    onSuccess: () => { toast.success("Stock adjusted"); qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["movements"] }); qc.invalidateQueries({ queryKey: ["notifications"] }); setReason(""); setQty(1); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });

  return (
    <div className="space-y-6" data-testid="stock-adjust-page">
      <div>
        <div className="label-cap">Inventory</div>
        <h1 className="font-heading text-3xl mt-1">Stock Adjustment</h1>
        <p className="text-sm text-agri-ink-2 mt-1">Record stock-in (purchases, returns) or stock-out (damage, expiry).</p>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (!pid) return toast.error("Select a product"); adj.mutate(); }} className="card-soft p-6 max-w-2xl space-y-4">
        <div>
          <label className={labelCls}>Product</label>
          <select data-testid="adjust-product-select" required value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls}>
            <option value="">Select product…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.stock_qty} {p.unit} in stock</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><label className={labelCls}>Adjustment type</label>
            <select data-testid="adjust-type-select" value={type} onChange={(e) => setType(e.target.value)} className={inputCls}>
              <option value="in">Stock In (received)</option>
              <option value="out">Stock Out (damaged/expired)</option>
            </select></div>
          <div><label className={labelCls}>Quantity</label>
            <NumberInput data-testid="adjust-qty-input" required emptyValue={1} value={qty}
              onChange={(n) => { setQty(n); fe.clearField("quantity"); }}
              className={fe.fieldError("quantity") ? inputErrCls : ""} />
            <FieldError>{fe.fieldError("quantity")}</FieldError></div>
        </div>
        <div><label className={labelCls}>Reason / Notes</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className={inputCls} placeholder="e.g. Damaged in transit, return from client…" /></div>
        <button data-testid="adjust-submit-btn" type="submit" className={btnPrimary} disabled={adj.isPending}>Submit Adjustment</button>
      </form>
    </div>
  );
}
